// First-login walkthrough. The integrations step reuses ConnectPage.
// Later steps are readiness, planning, and approval. Progress is the
// saved onboarding step, so a later visit opens the same step.
// Hidden unless VITE_PLAN_ONBOARDING=true. PATCH only records the step.

import { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { Box, Button, Heading, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { useBrand } from '../../brand/BrandContext';
import {
  advancePlanOnboarding, getPlanOnboarding, planOnboardingEnabled, stepLabel,
  type OnboardingReadiness, type OnboardingStep, type PlanOnboarding
} from '../../marketingPlans/onboarding';
import { ConnectPage } from './ConnectPage';

export function PlanOnboardingPage() {
  const { activeBrand } = useBrand();
  const brandId = activeBrand?.id || (typeof localStorage === 'undefined' ? null : localStorage.getItem('brand_id'));
  const enabled = planOnboardingEnabled();
  const [loading, setLoading] = useState(enabled);
  const [state, setState] = useState<PlanOnboarding | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !brandId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void getPlanOnboarding(brandId).then((next) => {
      if (cancelled) return;
      setState(next);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [enabled, brandId]);

  if (!enabled) return <Navigate to="/brand" replace />;
  if (!brandId) return <Muted>Select a brand to continue setup.</Muted>;
  if (loading) {
    return (
      <HStack data-testid="plan-onboarding-loading">
        <Spinner size="sm" />
        <Text fontSize="sm" color="brand.muted">Loading setup…</Text>
      </HStack>
    );
  }
  if (!state) return <Muted>Setup isn't available yet.</Muted>;
  if (state.step === 'done') return <Navigate to="/brand" replace />;
  if (state.step === 'integrations') {
    return (
      <VStack align="stretch" spacing={4}>
        <Checklist integrations={state.integrations} />
        <ConnectPage
          dispatchSyncs
          onFinished={() => move(brandId, 'readiness', setState, setError)}
        />
        {error && <Text fontSize="sm" color="red.600">{error}</Text>}
      </VStack>
    );
  }

  return (
    <Box maxW="640px" mx="auto" py={8} px={4} data-testid="plan-onboarding-step">
      <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
        {stepLabel(state.step)}
      </Text>
      {state.step === 'readiness' && <Readiness readiness={state.readiness} />}
      {state.step === 'plan' && (
        <VStack align="stretch" spacing={3}>
          <Heading size="md">Draft a first plan</Heading>
          <Text fontSize="sm" color="brand.muted">The planning chat can draft the first plan. This step does not create the plan by itself.</Text>
          <Button as={RouterLink} to="/home" alignSelf="flex-start" variant="outline">Open the planning chat</Button>
        </VStack>
      )}
      {state.step === 'approve' && (
        <VStack align="stretch" spacing={3}>
          <Heading size="md">Review and approve</Heading>
          <Text fontSize="sm" color="brand.muted">Review the plan on the brand page. Approving stays on the existing plan flow. Nothing here is pushed to Meta.</Text>
          <Button as={RouterLink} to="/brand" alignSelf="flex-start" variant="outline">Review plans</Button>
        </VStack>
      )}
      {error && <Text fontSize="sm" color="red.600" mt={3}>{error}</Text>}
      <Button mt={6} variant="brand" onClick={() => { void move(brandId, nextStep(state.step), setState, setError); }}>
        {state.step === 'approve' ? 'Mark setup complete' : 'Continue'}
      </Button>
    </Box>
  );
}

function Checklist({ integrations }: { integrations: PlanOnboarding['integrations'] }) {
  return (
    <Box px={4} pt={6} maxW="780px" mx="auto" data-testid="onboarding-checklist">
      <Heading size="sm" mb={2}>Integration checklist</Heading>
      {integrations.length === 0 ? (
        <Text fontSize="sm" color="brand.muted">Connection status isn't available yet.</Text>
      ) : (
        <VStack align="stretch" spacing={1}>
          {integrations.map((row) => (
            <Text key={row.provider} fontSize="sm">
              {row.provider}: {row.status}{row.path ? ` · ${row.path}` : ''}
            </Text>
          ))}
        </VStack>
      )}
    </Box>
  );
}

function Readiness({ readiness }: { readiness: OnboardingReadiness }) {
  return (
    <VStack align="stretch" spacing={2} data-testid="onboarding-readiness">
      <Heading size="md">Check readiness</Heading>
      <Text fontSize="sm">Catalog has products: {checkLabel(readiness.catalogHasProducts)}</Text>
      <Text fontSize="sm">In-stock colorways: {checkLabel(readiness.colorwaysInStock)}</Text>
      <Text fontSize="sm">Meta can be read: {checkLabel(readiness.metaReadable)}</Text>
      {readiness.note && <Text fontSize="sm" color="brand.muted">{readiness.note}</Text>}
    </VStack>
  );
}

function checkLabel(value: boolean | null): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Not checked yet';
}

function nextStep(step: OnboardingStep): OnboardingStep {
  if (step === 'readiness') return 'plan';
  if (step === 'plan') return 'approve';
  return 'done';
}

async function move(
  brandId: string,
  step: OnboardingStep,
  setState: (next: PlanOnboarding | null) => void,
  setError: (message: string | null) => void
): Promise<boolean> {
  setError(null);
  const next = await advancePlanOnboarding(brandId, step);
  if (!next) {
    setError("Couldn't save this step.");
    return false;
  }
  setState(next);
  return true;
}

function Muted({ children }: { children: string }) {
  return <Text fontSize="sm" color="brand.muted">{children}</Text>;
}
